import { isTotalFinanceDiagnosticsEnabled } from 'utils/shared/totalfinance/engineMode';
import { OpcPricingSession } from 'utils/shared/totalfinance/opcPricing';

interface Props {
  pricing: OpcPricingSession;
  /** Show the panel in TotalFinance mode too (NEXT_PUBLIC_TOTALFINANCE_DIAGNOSTICS). */
  diagnostics?: boolean;
}

const operationLabel = { point: 'price', curve: 'chart', table: 'table' } as const;

/**
 * Pricing-engine provenance for the OPC, for dogfooding and acceptance: always in shadow mode, and in
 * TotalFinance mode only when diagnostics are enabled. Users of a legacy or TotalFinance build see
 * nothing; disagreements still reach Sentry as reason codes, never a quote or position.
 */
export default function PricingEngineStatus({
  pricing,
  diagnostics = isTotalFinanceDiagnosticsEnabled(),
}: Props) {
  if (pricing.mode === 'legacy' || (pricing.mode === 'totalfinance' && !diagnostics)) return null;
  const comparisons = pricing.comparisons();
  const hasFallback = comparisons.some((result) => result.status !== 'match');
  const title =
    pricing.mode === 'shadow'
      ? 'TotalFinance shadow comparison · legacy values shown'
      : hasFallback
      ? 'TotalFinance pricing · legacy fallback on some calculations'
      : 'TotalFinance pricing';
  return (
    <aside
      className="mx-2 my-2 rounded-md border border-palette-black-3 bg-palette-black-4 px-3 py-2 text-xs"
      aria-label="Pricing engine status"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="font-semibold">{title}</p>
        {comparisons.map((result) => (
          <span
            key={result.operation}
            className={result.status === 'match' ? 'text-green-400' : 'text-amber-200'}
          >
            {operationLabel[result.operation]}:{' '}
            {result.status === 'match'
              ? pricing.mode === 'shadow'
                ? 'agrees'
                : 'TotalFinance'
              : `legacy (${result.status}${
                  result.reasons.length ? `: ${result.reasons.join(', ')}` : ''
                })`}
          </span>
        ))}
      </div>
      <details className="mt-1">
        <summary className="cursor-pointer text-palette-black-7">About this calculation</summary>
        <p className="mt-1">
          Chart, table and time-slider valuations use the same selected legs and app conventions
          (European pricing proxy, no dividends or early exercise, ACT/365.25 to the 16:00 ET
          expiry). Each surface shows TotalFinance only while it agrees with the legacy engine
          within per-contract tolerances; otherwise that surface shows legacy. Probability and
          strategy-summary heuristics remain app calculations. This is not a live trading engine.
        </p>
        <p className="mt-1">
          Rollback: set OPC_CALCULATION_ENGINE to &apos;legacy&apos; in constants/flags.ts, or
          NEXT_PUBLIC_TOTALFINANCE_OPC_MODE=legacy, and rebuild. Disagreements are reported to error
          monitoring as reason codes; the report carries no quote or position.
        </p>
      </details>
    </aside>
  );
}
